import moment from '@/lib/time';
import Excel from 'exceljs';
import AxiosService from '@/plugins/axios';
import endpoints from '../../config/config';
import {
  fontNormal,
  sheetMargin,
  hightlightRow,
  hightlightTableHead,
  blobToFile,
  formatDate,
  generateLogoHeader,
  addLogoCompany,
} from './config';

const tableHead = [
  'ทะเบียน',
  'เวลา',
  'ประเภทแจ้งเตือน',
  'สถานที่',
  'alarm source',
  'alarm level',
  'alarm status',
  'alarm speed',
  'alarm sign',
  'รูปภาพ'
];
const columnsSpec = [
  {
    key: 'plate_number',
    width: 20,
    style: {
      numFmt: '0',
      font: fontNormal,
    },
  },
  {
    key: 'start_time',
    width: 20,
  },
  {
    key: 'type',
    width: 30,
  },
  {
    key: 'location',
    width: 28,
  },
  {
    key: 'alarm_source',
    width: 18,
  },
  {
    key: 'alarm_level',
    width: 18,
  },
  {
    key: 'alarm_status',
    width: 18,
  },
  {
    key: 'alarm_speed',
    width: 18,
  },
  {
    key: 'alarm_sign',
    width: 18,
  },
  {
    key: 'images',
    width: 20
  }
];

async function fetchImageBuffer(url) {
  try {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();

    return arrayBuffer;
  } catch (err) {
    console.error(err)
    return null
  }
}

function getExtension(url) {
  const ext = url.split('.').pop().split('?')[0].toLowerCase();
  const map = { jpg: 'jpeg', jpeg: 'jpeg', png: 'png', gif: 'gif' };
  return map[ext] || 'png';
}

function splitReportByVehicle(rawData) {
  let result = {};

  rawData.forEach(data => {
    result[data.licenseNum] = result[data.licenseNum] || [];

    result[data.licenseNum].push(data);
  });

  return result
}

async function generateExcel(fileName, reportByVehicle, startUnix, endUnix, logo) {
  const workbook = new Excel.Workbook();
  const pageProperties = {
    properties: {
      tabColor: {
        argb: 'FF41C173',
      },
      defaultColWidth: 20
    },
    pageSetup: {
      paperSize: 9,
      orientation: 'portrait',
      fitToPage: true,
    },
  };
  let imageLogoId;

  if (logo && logo.url) {
    const ext = logo.ext.substring(1);
    const myBase64Image = await generateLogoHeader(logo);
    imageLogoId = workbook.addImage({
      base64: myBase64Image,
      extension: ext,
    });
  }

  if (Object.keys(reportByVehicle).length > 0) {
    for (const plateNumber of Object.keys(reportByVehicle)) {

      let startingRow = 1;
      const sheet = workbook.addWorksheet(plateNumber, pageProperties);
      sheet.pageSetup.margins = sheetMargin;
      sheet.columns = columnsSpec;

      const metaRow1 = `รายงานแจ้งเตือนความปลอดภัย ของ รถทะเบียน ${plateNumber} เวลาเริ่ม ${formatDate(startUnix)} ถึง ${formatDate(endUnix)}`;

      if (logo && logo.url) {
        startingRow = 2;
        addLogoCompany(sheet, imageLogoId);
      }

      sheet.addRow([metaRow1]);
      sheet.addRow([]);
      sheet.addRow(tableHead);

      for (let i in reportByVehicle[plateNumber]) {
        const data = reportByVehicle[plateNumber][i];

        const row = sheet.addRow({
          plate_number: data.licenseNum,
          start_time: data.startTime,
          type: data.alarmType,
          location: data.location,
          alarm_source: data.alarmSource,
          alarm_level: data.alarmLevel,
          alarm_status: data.alarmStatus,
          alarm_speed: data.alarmSpeed,
          alarm_sign: data.alarmSign,
        });

        if (data.images.length > 0) {
          const buffers = await Promise.all(data.images.map(async (url) => await fetchImageBuffer(url)));

          for (let i = 0; i <= buffers.length - 1; i++) {
            const buffer = buffers[i];
            if (buffer) {
              row.height = 90;
              row.alignment = { vertical: 'middle' };

              const url = data.images[i];
              const imageId = workbook.addImage({ buffer, extension: getExtension(url) });
              sheet.addImage(imageId, {
                tl: {
                  col: 9.1 + i,
                  row: row.number - 0.9,
                },
                ext: { width: 140, height: 140 },
              });
            }
          }
        }
      }

      hightlightTableHead(sheet, startingRow + 2);
      sheet.getRow(startingRow + 2).alignment = { horizontal: 'center' };
      hightlightRow(sheet, startingRow);
    }
  }
  else {
    workbook.addWorksheet('ไม่พบข้อมูล', pageProperties);
  }

  return blobToFile(workbook, fileName);
}

async function getSafetyAlarmReport(plateNumber, startUnix, endUnix, logo) {
  const startTime = moment.unix(startUnix);
  const endTime = moment.unix(endUnix);

  const data = await AxiosService.post(`${endpoints.richmorApi}/safety-alarm-list`, {
    license_num: plateNumber,
    start_time: startTime.format('YYYY-MM-DD HH:mm:ss'),
    end_time: endTime.format('YYYY-MM-DD HH:mm:ss')
  }).then((response) => {
    const responseBody = response.data;
    return responseBody.data || [];
  }).catch((error) => {
    console.log('An error occurred:', error);
    return []
  });

  const reportByVehicle = splitReportByVehicle(data);

  const strTimerange =
    startTime.format('D_MMM_YY') +
    '-ถึง-' +
    endTime.format('D_MMM_YY');
  const fileName = 'Safety_Alarm_Report_' + strTimerange;

  return await generateExcel(fileName, reportByVehicle, startUnix, endUnix, logo)
}

export default getSafetyAlarmReport;
